<script setup>
import { computed, onMounted, onUnmounted } from 'vue'
import { useRoute, RouterLink, RouterView } from 'vue-router'
import Nav from './views/Nav/NavView.vue'
import DesktopLoginModal from '@/components/web/DesktopLoginModal.vue'
import GlobalPikachuLoading from '@/components/GlobalPikachuLoading.vue'
import { getReqData, InitAPI, TokenLoginAPI, GetUserInfoAPI } from '@/util/api'
import { initConfigStore, useInfoStore } from '@/stores/counter.js'
import encipher from '@/util/mySEC'
import { shouldUseMobileLayout } from '@/util/mobileMode'

const route = useRoute()
const initStore = initConfigStore()
const userStore = useInfoStore()

// Mobile pages share the same fixed bottom navigation.  Several detail routes
// intentionally hide the desktop navigation, so route.meta.showNav alone must
// not suppress the mobile bar.
const showGlobalNav = computed(() => route.meta.showNav || shouldUseMobileLayout())
const isMobileApp = computed(() => shouldUseMobileLayout())

// Restore the cached session synchronously before child routes mount.
// Otherwise protected pages can briefly see an empty Pinia store on refresh
// and incorrectly open the login modal while InitAPI is still pending.
const restoreCachedUser = () => {
  const cachedUser = localStorage.getItem('nsjkdjc==')
  if (!cachedUser) return

  try {
    const restoredUser = JSON.parse(encipher.des_decrypt(cachedUser))
    if (restoredUser?.uid && restoredUser?.token) {
      userStore.setUserInfo(restoredUser)
      userStore.setShowLoginModal(false)
    }
  } catch (error) {
    console.warn('[session] Failed to restore cached user.', error)
  }
}

restoreCachedUser()

const tokenLogin = (tokenData) => {
  TokenLoginAPI(getReqData(tokenData))
    .then((res) => {
      if (res.data.state == 1) {
        localStorage.setItem('nsjkdjc==', encipher.des_encrypt(res.data.data))
        userStore.setUserInfo(res.data.data)
        getUserInfo(res.data.data)
      } else {
        console.log('[error]', res.data.msg)
      }
    })
    .catch((err) => {
      console.log('[error]', err)
    })
}

const getUserInfo = (data) => {
  let reqData = {
    uname: data.uname,
    uid: data.uid,
    token: data.token
  }
  reqData = Object.assign({ pgid: '' }, reqData, JSON.parse(localStorage.getItem('href-url')))
  GetUserInfoAPI(getReqData(reqData))
    .then((res) => {
      if (res.data.state == 1) {
        userStore.setUserInfo(Object.assign(data, res.data.data))
        localStorage.setItem('nsjkdjc==', encipher.des_encrypt(Object.assign(data, res.data.data)))
      } else {
        showToast({ type: 'fail', message: res.data.msg, className: 'toast-fail' })
      }
    })
    .catch((err) => {
      console.log('[error]', err)
    })
}

const updateModalScale = () => {
  if (shouldUseMobileLayout()) {
    // 假设基准宽度为420px，缩放比例为 90%
    let scale = (window.innerWidth * 0.9) / 420
    if (scale > 1) scale = 1
    document.documentElement.style.setProperty('--modal-scale', scale)
  } else {
    document.documentElement.style.setProperty('--modal-scale', 1)
  }
}

const handleSessionExpired = () => {
  userStore.setUserInfo('')
  userStore.setShowLoginModal(true)
}

onMounted(() => {
  updateModalScale()
  window.addEventListener('resize', updateModalScale)
  window.addEventListener('session-expired', handleSessionExpired)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateModalScale)
  window.removeEventListener('session-expired', handleSessionExpired)
})

const initApp = () => {
  let urlStr = window.location.href.split('?')[1]
  const urlSearchParams = new URLSearchParams(urlStr)
  // let initParams = {}
  // if (localStorage.getItem('href-url')) {
  //   initParams = JSON.parse(localStorage.getItem('href-url'))
  // } else {
  let initParams = Object.fromEntries(urlSearchParams.entries())
  if (initParams.wv) {
    let initData = JSON.parse(JSON.stringify(initParams))
    if (initData.uname) {
      delete initData.uname
    }
    if (initData.uid) {
      delete initData.uid
    }
    if (initData.token) {
      delete initData.token
    }
    localStorage.setItem('href-url', JSON.stringify(initData))
  } else {
    if (localStorage.getItem('href-url')) {
      initParams = JSON.parse(localStorage.getItem('href-url'))
    } else {
      return
    }
  }
  // }
  initParams.clientid = localStorage.getItem('browserId')
  if (initParams.wv != 1 && !initParams.clientid) {
    let initTimeOut = setTimeout(() => {
      initApp()
      clearTimeout(initTimeOut)
    }, 500)
    return
  }
  if (localStorage.getItem('install')) {
    initParams.install = 0
  } else {
    initParams.install = 1
    localStorage.setItem('install', 1)
  }
  initParams.os = ''
  InitAPI(getReqData(initParams))
    .then((res) => {
      if (res.data.state == 1) {
        initStore.setInitConfig(res.data.data)
        localStorage.setItem('initdevice==', encipher.des_encrypt(res.data.data))
        if (initParams.token) {
          tokenLogin(initParams)
        } else {
          if (localStorage.getItem('nsjkdjc==')) {
            userStore.setUserInfo(
              JSON.parse(encipher.des_decrypt(localStorage.getItem('nsjkdjc==')))
            )
          }
        }
      } else {
        console.log('[error]', res.data.msg)
      }
    })
    .catch((err) => {
      console.log('[error]', err)
    })
}
initApp()
</script>

<template>
  <section class="app-content" :class="{ 'is-mobile-app': isMobileApp }">
    <RouterView />
    <Nav v-show="showGlobalNav" />
    <DesktopLoginModal
      v-if="userStore.showLoginModal"
      @close="userStore.setShowLoginModal(false)"
    />
    <GlobalPikachuLoading />
  </section>
</template>

<style scoped>
.app-content {
  max-width: 100vw;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  /* background: orange; */
  /* box-shadow: 0px 0px 2px 2px #bbbbbb; */
}
.app-content.is-mobile-app {
  width: 100%;
  max-width: 430px;
  min-width: 0;
  min-height: 100vh;
  min-height: 100dvh;
  height: auto;
  margin: 0 auto;
  overflow-x: clip;
  padding-bottom: calc(min(13.4667vw, 58px) + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  background: #fdf6cc;
}
@media screen and (min-device-width: 920px) {
  .app-content {
    width: 100%;
    min-height: 100vh;
    height: auto;
    display: flex;
    flex-direction: column;
    /* background: #ffffff; */
  }
}
</style>
